import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { SharingInvitationStatus } from '@/shared/api/protocol-ts/model/dto_sharing_pb';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Avatar, Skeleton, Tooltip } from '@/shared/ui';

import { accessModel } from '@/entities/access';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';

import {
  RevokeSharedPatientModal,
  SharePatientModalForm,
} from '@/features/sharePatient';
import { ShowAllSharedWithModal } from '@/features/showAllSharedWithList';

import { MAX_ROWS_COUNT } from '../../config';
import { getShortedList } from '../../lib';
import { PeopleCard } from '../PeopleCard/PeopleCard';

type SharedWithInlineProps = {
  patientID: string;
  className?: string;
};

export const SharedWithInline: FC<SharedWithInlineProps> = (props) => {
  const { patientID, className } = props;

  const dispatch = useAppDispatch();
  const invitations = useAppSelector(
    accessModel.sharingSelectors
      .selectAcceptedOrPendingSharedPatientInvitations,
  );
  const loading = useAppSelector(
    accessModel.sharingSelectors.selectSharingLoading,
  );
  const { CanViewSharings, CanShare } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );
  const isForbidSharing = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_Sharing,
    ),
  );

  if (isForbidSharing || !CanViewSharings) {
    return null;
  }

  if (loading === 'pending') {
    return (
      <Skeleton
        className={className}
        borderRadius={16}
        height={56}
        width="100%"
      />
    );
  }

  const openShowAllModal = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.AllSharedWith,
        data: { patientID },
      }),
    );
  };

  const openSharePatientModal = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.SharePatient,
        data: { patientID },
      }),
    );
  };

  // An auto-accepted invitation to a doctor without an account stays Pending
  // on the backend but is already effective, so it is not counted as pending.
  const pendingCount = invitations.filter(
    (invitation) =>
      invitation.Status ===
        SharingInvitationStatus.SharingInvitationStatus_Pending &&
      !invitation.AutoAccept,
  ).length;
  const invitationsCount = invitations.length;
  const visibleInvitations = getShortedList(invitations);
  const hiddenCount = invitationsCount - MAX_ROWS_COUNT;

  return (
    <>
      <PeopleCard
        className={className}
        dataSection="shared-with"
        label={
          <FormattedMessage
            id="patientInfo.sharedWith"
            defaultMessage="Shared with"
          />
        }
        onOpen={invitationsCount > 0 ? openShowAllModal : undefined}
        action={
          CanShare && (
            <PeopleCard.ActionButton onClick={openSharePatientModal}>
              <FormattedMessage id="global.share" defaultMessage="Share" />
            </PeopleCard.ActionButton>
          )
        }
      >
        {invitationsCount === 0 ? (
          <PeopleCard.Muted>
            <FormattedMessage
              id="patientInfo.notShared"
              defaultMessage="Not shared yet"
            />
          </PeopleCard.Muted>
        ) : (
          <>
            <Avatar.Group>
              {visibleInvitations.map((invitation) => {
                const recipient =
                  invitation.Recipient?.Email || invitation.Recipient?.Phone;

                return (
                  <Tooltip.Third
                    key={invitation.ID}
                    content={recipient}
                    delayDuration={0}
                    side="right"
                    align="end"
                    alignOffset={-9}
                  >
                    <Avatar
                      size={32}
                      tinted
                      initials={recipient?.at(0)?.toUpperCase()}
                    />
                  </Tooltip.Third>
                );
              })}
              {hiddenCount > 0 && <Avatar size={32} count={hiddenCount} />}
            </Avatar.Group>
            <PeopleCard.Count>{invitationsCount}</PeopleCard.Count>
            {pendingCount > 0 && (
              <PeopleCard.Muted>
                {'· '}
                <FormattedMessage
                  id="patientInfo.pendingCount"
                  defaultMessage="{count} pending"
                  values={{ count: pendingCount }}
                />
              </PeopleCard.Muted>
            )}
          </>
        )}
      </PeopleCard>

      <SharePatientModalForm />
      <RevokeSharedPatientModal />
      <ShowAllSharedWithModal />
    </>
  );
};
